import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, IndianRupee, Zap, ShieldCheck, ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatINR } from "@/lib/api";

const TICKERS = [
  { model: "iPhone 15 Pro Max 256GB", price: 89500, when: "2m ago" },
  { model: "Samsung S24 Ultra 512GB", price: 82000, when: "6m ago" },
  { model: "OnePlus 12 5G 256GB", price: 44000, when: "9m ago" },
  { model: "Pixel 9 Pro XL", price: 75000, when: "14m ago" },
  { model: "iPhone 14 Pro 128GB", price: 54000, when: "22m ago" },
];

export default function HeroSection() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % TICKERS.length), 3200);
    return () => clearInterval(t);
  }, []);
  const tk = TICKERS[i];

  return (
    <section className="relative overflow-hidden tv-hero-dark">
      <div className="absolute inset-0 tv-hero-dark-grid opacity-70 pointer-events-none" />
      {/* Big logo watermark background */}
      <img
        src="/tech-value-logo.png"
        alt=""
        aria-hidden
        className="absolute -right-16 -top-16 w-[420px] sm:w-[520px] lg:w-[620px] h-auto opacity-[0.08] pointer-events-none select-none tv-float"
      />
      {/* Decorative glowing orbs */}
      <div className="absolute -left-20 top-40 w-72 h-72 rounded-full bg-amber-500/20 blur-[100px] pointer-events-none" />
      <div className="absolute right-10 bottom-10 w-96 h-96 rounded-full bg-accent/20 blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="tv-chip-white" data-testid="hero-chip">
              <Sparkles className="w-3.5 h-3.5" /> AI Assessment · Spot Cash
            </span>
            <span className="tv-chip-emerald">
              <Star className="w-3.5 h-3.5 fill-current" /> 4.9 · 12,400+ pickups
            </span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02]">
            Sell your <span className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 bg-clip-text text-transparent">phone</span>.
            <br />
            Buy <span className="bg-gradient-to-r from-emerald-300 to-accent bg-clip-text text-transparent">refurbished</span>.
            <br />
            <span className="text-3xl sm:text-4xl lg:text-5xl text-slate-300 font-bold">Tech Value — apne phone ko dijiye sahi keemat.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
            Instant AI-verified quote for iPhone, Samsung, OnePlus, Pixel & more. Free doorstep pickup across
            Mumbai · guaranteed cash / UPI on the spot. Or shop premium-grade refurbished phones with warranty.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              size="lg"
              className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:opacity-90 text-white h-12 px-6 shadow-xl shadow-amber-500/40 font-bold"
              onClick={() => document.getElementById("brands")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-get-quote-btn"
            >
              Get Instant Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a href="/shop" data-testid="hero-shop-link">
              <Button
                size="lg"
                variant="outline"
                className="h-12 px-6 border-white/25 text-white bg-white/5 hover:bg-white/10 backdrop-blur"
              >
                Shop Refurbished
              </Button>
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
            <Stat value="45s" label="Instant Quote" />
            <Stat value="7 days" label="Price Lock" />
            <Stat value="0₹" label="Advance Fee" />
          </div>
        </div>

        {/* Right - live valuation preview card */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="tv-glass-dark rounded-3xl p-6 shadow-2xl relative"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="tv-chip-emerald">
                <Zap className="w-3 h-3" /> Live Payout
              </div>
              <span className="text-xs font-mono text-slate-400">TW-AI · Verified</span>
            </div>
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2"
            >
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400">
                Recently Paid
              </div>
              <div className="mt-1 text-xl font-semibold">{tk.model}</div>
              <div className="mt-4 flex items-end gap-2">
                <span className="text-5xl font-extrabold font-mono text-accent flex items-center">
                  <IndianRupee className="w-7 h-7" />
                  {tk.price.toLocaleString("en-IN")}
                </span>
                <span className="pb-2 text-xs font-mono text-slate-400">/ {tk.when}</span>
              </div>
            </motion.div>

            <div className="mt-6 grid grid-cols-3 gap-2">
              {TICKERS.slice(0, 3).map((t, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-white/10 bg-white/5 p-3 text-[11px] leading-tight"
                >
                  <div className="font-medium truncate">{t.model.split(" ").slice(0, 3).join(" ")}</div>
                  <div className="font-mono text-emerald-400 font-semibold mt-1">{formatINR(t.price)}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3 text-xs text-slate-300">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>Price locked for 7 days · Free doorstep pickup</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
            <div className="border-l-2 border-amber-400/60 pl-3">
              <div className="font-mono font-black text-2xl text-white">{value}</div>
              <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
            </div>
  );
}
